import { Injectable } from '@angular/core';
import { IKeyValue } from '@carm/_utils/models';
import { TranslateService } from '@ngx-translate/core';
import {
  IBaseDutiesAndTaxes,
  ICadGoodResponse,
  ICadInvoiceResponse,
} from '../models/api';
import { CalculatedViews } from '../models/common';
import { IDeclarationTotalItem, IDeclarationTotalList } from '../models/form';

@Injectable()
export class DutiesAndTaxesHelperService {
  constructor(public translateService: TranslateService) {}

  public mapToDeclarationTotals(
    view: CalculatedViews,
    dutyTaxFees: IBaseDutiesAndTaxes,
    invoices: ICadInvoiceResponse[],
    goodsKV: IKeyValue<string, ICadGoodResponse[]>[],
    typeUid: string = null
  ): IDeclarationTotalList {
    const results: IDeclarationTotalItem[] = [];
    let totalDutiesAndTaxes = 0;
    let totalInterest = 0;
    let totalDutiesAndTaxesWithInterest = 0;
    let totalVfdAmount = 0;
    let exchangeRate = null;

    switch (view) {
      case CalculatedViews.DECLARATION_TOTALS:
        const declarationTotals = this.getDeclarationItems(dutyTaxFees);
        results.push(...declarationTotals);
        totalDutiesAndTaxes = dutyTaxFees?.totalAmount;
        totalInterest = dutyTaxFees?.totalInterest;
        totalVfdAmount = dutyTaxFees?.vfdAmount;
        totalDutiesAndTaxesWithInterest = dutyTaxFees?.totalDutTaxWInt;
        exchangeRate = null;
        break;
      case CalculatedViews.VENDOR_TOTALS:
        if (invoices && invoices.length > 0) {
          const vendorTotals: IDeclarationTotalItem[] = [];

          invoices
            .filter(source => source.invoiceUid === typeUid)
            .forEach(source => {
              const vendorDuties = this.getDeclarationItems(source.dutyTaxFees);
              if (vendorTotals.length === 0) {
                vendorTotals.push(...vendorDuties);
                totalDutiesAndTaxes = source.dutyTaxFees?.totalAmount;
                totalInterest = source.dutyTaxFees?.totalInterest;
                totalVfdAmount = source.dutyTaxFees?.vfdAmount;
                totalDutiesAndTaxesWithInterest = source.dutyTaxFees?.totalDutTaxWInt;
                exchangeRate = null;
              } else {
                vendorTotals.forEach(vendorDuty => {
                  const duty = vendorDuties.find(
                    item => item.type.key === vendorDuty.type.key
                  );
                  if (duty) {
                    vendorDuty.amount += duty.amount;
                    if(duty.amount > 0) { // don't sum null value
                      vendorDuty.amount += duty.amount;
                    }

                    vendorDuty.total += duty.total || null;
                    totalDutiesAndTaxes += vendorDuty.total;
                    totalInterest += vendorDuty.totalInterest;
                    totalVfdAmount += vendorDuty.totalVfdAmount;
                    totalDutiesAndTaxesWithInterest += vendorDuty.totalWithInterest;
                    exchangeRate = null;
                  }
                });
              }
            });
          results.push(...vendorTotals);
        }
        break;
      case CalculatedViews.GOOD_TOTALS:
        if (goodsKV && goodsKV.length > 0) {
          const allGoods = Array.from(goodsKV.values())
            .map(x => x.value)
            .reduce((goods1, goods2) => goods1.concat(goods2));

          const goodTotals: IDeclarationTotalItem[] = [];
          allGoods
          .filter(source => source.goodUid === typeUid)
          .forEach(good => {
            const goodDuties = this.getDeclarationItems(good.dutyTaxFees);
            if (goodTotals.length === 0) {
              goodTotals.push(...goodDuties);
              totalDutiesAndTaxes = good.dutyTaxFees?.totalAmount;
              totalInterest = good.dutyTaxFees?.totalInterest;
              totalVfdAmount = good.dutyTaxFees?.vfdAmount;
              totalDutiesAndTaxesWithInterest = good.dutyTaxFees?.totalDutTaxWInt;
              exchangeRate = good.exchangeRate;
            } else {
              goodTotals.forEach(goodDuty => {
                const duty = goodDuties.find(
                  item => item.type.key === goodDuty.type.key
                );
                if (duty) {
                  if(duty.amount > 0) { // don't sum null value
                    goodDuty.amount += duty.amount;
                  }
                  goodDuty.total += duty.total || null;
                  totalDutiesAndTaxes += goodDuty.total;
                  totalInterest += goodDuty.totalInterest;
                  totalVfdAmount += goodDuty.totalVfdAmount;
                  totalDutiesAndTaxesWithInterest += goodDuty.totalWithInterest;
                  exchangeRate = goodDuty.exchangeRate;
                }
              });
            }
          });
          results.push(...goodTotals);
        }
        break;
    }

    return {
      results,
      totals: {
        total: totalDutiesAndTaxes,
        totalInterest: totalInterest,
        totalVfdAmount: totalVfdAmount,
        totalWithInterest: totalDutiesAndTaxesWithInterest,
        exchangeRate: exchangeRate
      } as IDeclarationTotalItem,
    } as IDeclarationTotalList;
  }

  private getDeclarationItems(
    dutyTaxFees: IBaseDutiesAndTaxes
  ): IDeclarationTotalItem[] {
    const listOfTaxes: IDeclarationTotalItem[] = [
      {
        type: {
          key: 'pstHST',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.pst_hst'
          ),
        },
        amount: null,
        relieved: null,
        total: dutyTaxFees.pstHSTNet,
      },
      {
        type: {
          key: 'customsDuty',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.customs_duty'
          ),
        },
        amount: dutyTaxFees.customsDutyTotal,
        relieved: dutyTaxFees.totalCustomDutyRemission,
        total: dutyTaxFees.customsDutyNet,
      },
      {
        type: {
          key: 'exciseDuty',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.excise_duties'
          ),
        },
        amount: dutyTaxFees.exciseDutyTotal,
        relieved: dutyTaxFees.totalExciseDutyRemission,
        total: dutyTaxFees.exciseDutyNet,
      },
      {
        type: {
          key: 'exciseTax',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.excise_tax'
          ),
        },
        amount: dutyTaxFees.exciseTaxTotal,
        relieved: dutyTaxFees.totalExciseTaxRemission,
        total: dutyTaxFees.exciseTaxNet,
      },
      {
        type: {
          key: 'gst',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.GST'
          ),
        },
        amount: dutyTaxFees.gstTotal,
        relieved: dutyTaxFees.totalGSTRemission,
        total: dutyTaxFees.gstNet,
      },
      {
        type: {
          key: 'alcohol',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.alcohol'
          ),
        },
        amount: null,
        relieved: null,
        total: dutyTaxFees.totalProvAlcohol,
      },
      {
        type: {
          key: 'cannabis',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.cannabis'
          ),
        },
        amount: null,
        relieved: null,
        total: dutyTaxFees.totalPSTCannabis,
      },
      {
        type: {
          key: 'tobacco',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.tobacco'
          ),
        },
        amount: null,
        relieved: null,
        total: dutyTaxFees.totalProvTobacco,
      },
      {
        type: {
          key: 'surtax',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.surtax'
          ),
        },
        amount: dutyTaxFees.surtaxTotal,
        relieved: dutyTaxFees.totalSurtaxRemission,
        total: dutyTaxFees.surtaxNet,
      },
      {
        type: {
          key: 'safeguard',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.safeguard'
          ),
        },
        amount: dutyTaxFees.safeguardTotal,
        relieved: dutyTaxFees.totalSafeguardRemission,
        total: dutyTaxFees.safeguardNet,
      },
      {
        type: {
          key: 'antiDumpSIMA',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.antidumping'
          ),
        },
        amount: dutyTaxFees.antiDumpSIMATotal,
        relieved: dutyTaxFees.totalAntiDumpingRemission,
        total: dutyTaxFees.antiDumpSIMANet,
      },
      {
        type: {
          key: 'counterSIMA',
          value: this.translateService.instant(
            'concepts.declarations.review_submit.taxes.countervailing'
          ),
        },
        amount: dutyTaxFees.counterSIMATotal,
        relieved: dutyTaxFees.totalCounterRemission,
        total: dutyTaxFees.counterSIMANet,
      },
    ];

    return listOfTaxes.sort((a, b) => (a.type?.value.toLowerCase() > b.type?.value.toLowerCase()) ? 1 : -1); // sort alphabetically;
  }
}
